import { useEffect } from "react";

import type { ExternalServiceConfig as ExternalService } from "api/schemas";

const HEAD_ID = "remote-gtm-head";
const BODY_ID = "remote-gtm-body";

function injectHeadSnippet(
  snippet: NonNullable<ExternalService["snippets"]>[number]
) {
  if (document.getElementById(HEAD_ID)) return; // already added

  const elementTag = snippet.tag === "noscript" ? "noscript" : "script";
  const element = document.createElement(elementTag);
  element.id = HEAD_ID;
  // Your API already returns just the JS function body or HTML content, so we drop it inside the element
  element.innerHTML = snippet.code ?? "";
  document.head.prepend(element);
}

function injectBodySnippet(
  snippet: NonNullable<ExternalService["snippets"]>[number]
) {
  if (document.getElementById(BODY_ID)) return;

  const elementTag = snippet.tag === "script" ? "script" : "noscript";
  const element = document.createElement(elementTag);
  element.id = BODY_ID;

  element.innerHTML = snippet.code ?? "";
  document.body.prepend(element);
}

function removeSnippet(id: string) {
  const existing = document.getElementById(id);
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }
}

export function useGtmExternalService({
  externalServices,
  propertySlug,
}: {
  externalServices: ExternalService[];
  propertySlug?: string;
}) {
  useEffect(() => {
    try {
      const gtmItem = externalServices?.find(
        (s) => s.type === "GoogleAnalytics"
      );

      if (!gtmItem || !gtmItem.snippets) return;

      type Snippet = NonNullable<ExternalService["snippets"]>[number];

      const headSnippet = gtmItem.snippets.find(
        (s: Snippet) => s.placement === "head"
      );

      const bodySnippet = gtmItem.snippets.find(
        (s: Snippet) => s.placement === "body"
      );

      if (headSnippet?.code) {
        injectHeadSnippet(headSnippet);
      }

      if (bodySnippet?.code) {
        injectBodySnippet(bodySnippet);
      }
    } catch (err) {
      console.error("Error loading GTM config", err);
    }

    return () => {
      removeSnippet(HEAD_ID);
      removeSnippet(BODY_ID);
    };
  }, [externalServices, propertySlug]);
}
